import { shouldOuterPageIgnoreKey } from '../../utils/canvasEditorModalKeyGuard';
import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
} from '../../types/domain';
import UnitLessonCountDelimiterModal from '../common/UnitLessonCountDelimiterModal';
import CrossCurricularCatalogPickerModal from '../program/CrossCurricularCatalogPickerModal';
import {
  getCrossTopicAndNoteForDisplay,
  parseMemoFieldStorageDeep,
} from '../../utils/programCellDisplayText';
import { computeLessonCountAssignments } from '../../utils/learningContentUnitGroupKey';
import {
  loadLessonCountUnitDelimiter,
  saveLessonCountUnitDelimiter,
} from '../../utils/lessonCountUnitDelimiterStorage';

type ColumnKey =
  | 'publisher'
  | 'track_text'
  | 'seq_text'
  | 'unit_text'
  | 'learning_text'
  | 'page_text'
  | 'assistant_page_text'
  | 'target_lesson_text'
  | 'total_lesson_text'
  | 'material_text'
  | 'achievement_text'
  | 'record_cross_curricular_text'
  | 'record_memo_text'
  | 'record_evaluation_text';

const BASE_COLUMNS: Array<{ key: ColumnKey; label: string; className: string }> = [
  { key: 'publisher', label: '출판사', className: 'cp-admin-learning-col-publisher' },
  { key: 'track_text', label: '편제', className: 'cp-admin-learning-col-track' },
  { key: 'seq_text', label: '순', className: 'cp-admin-learning-col-seqtext' },
  { key: 'unit_text', label: '단원', className: 'cp-admin-learning-col-unit' },
  { key: 'learning_text', label: '학습내용', className: 'cp-admin-learning-col-learning' },
  { key: 'page_text', label: '쪽수', className: 'cp-admin-learning-col-page' },
  { key: 'assistant_page_text', label: '보조쪽수', className: 'cp-admin-learning-col-assistant' },
  { key: 'target_lesson_text', label: '해당차시', className: 'cp-admin-learning-col-target' },
  { key: 'total_lesson_text', label: '전체차시', className: 'cp-admin-learning-col-total' },
  { key: 'material_text', label: '준비물', className: 'cp-admin-learning-col-material' },
  { key: 'achievement_text', label: '성취기준', className: 'cp-admin-learning-col-achievement' },
];
const PROGRAM_RECORD_COLUMNS: Array<{ key: ColumnKey; label: string; className: string }> = [
  { key: 'record_cross_curricular_text', label: '범교과', className: 'cp-admin-learning-col-record-cross' },
  { key: 'record_memo_text', label: '메모', className: 'cp-admin-learning-col-record-memo' },
  { key: 'record_evaluation_text', label: '평가', className: 'cp-admin-learning-col-record-eval' },
];
const MIN_VISIBLE_ROW_COUNT = 500;

export interface AdminLearningContentTableRow extends AdminLearningContentRowUpsert {
  _localId: string;
}

interface AdminLearningContentTableProps {
  workspaceId: string | null;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
  /** 툴바 왼쪽에 표시할 제목 (예: N학년 1학기 - 국어) */
  toolbarTitle?: string;
  rows: AdminLearningContentRow[];
  loading?: boolean;
  saving?: boolean;
  onSave: (rows: AdminLearningContentRowUpsert[]) => Promise<void>;
  showInlineActionButtons?: boolean;
  onBindActions?: (actions: {
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
  } | null) => void;
  onActionStateChange?: (state: { canUndo: boolean; canRedo: boolean; canSave: boolean }) => void;
  /** true면 셀 편집·행 조작·붙여넣기 등 변경 불가(복사·선택·화살표 이동만 허용) */
  readOnly?: boolean;
  /** true면 출판사 필터 등으로 행이 적을 때 500행 패딩을 쓰지 않고, 데이터 행+소량 여유만 둔다(빈 출판사 행 난립 방지). */
  slimPublisherFilterPadding?: boolean;
  /**
   * true(기본): 저장 시 모든 칸이 비어 있는 행은 제외.
   * false: 아래쪽 연속 빈 행(패딩)만 제외하고, 맨 아래로 한 번이라도 데이터가 있는 행까지의 구간은 그대로 저장해 중간 빈 행을 유지(관리자 학습내용 등).
   */
  omitEmptyRowsOnSave?: boolean;
  /** true면 범교과/메모/평가 열을 표시한다(기본 학습내용 등록 전용). */
  showProgramRecordColumns?: boolean;
}

function emptyRow(
  workspaceId: string | null,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  orderIndex: number
): AdminLearningContentTableRow {
  return {
    _localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    workspace_id: workspaceId,
    grade_number: gradeNumber,
    sub_category: subCategory,
    template_name: '',
    order_index: orderIndex,
    publisher: '',
    track_text: '',
    seq_text: '',
    unit_text: '',
    learning_text: '',
    page_text: '',
    assistant_page_text: '',
    target_lesson_text: '',
    total_lesson_text: '',
    material_text: '',
    achievement_text: '',
    record_cross_curricular_text: '',
    record_memo_text: '',
    record_evaluation_text: '',
  };
}

function toLocalRows(
  rows: AdminLearningContentRow[],
  workspaceId: string | null,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  padTarget: number = MIN_VISIBLE_ROW_COUNT,
  padBoostAfterData = 25
): AdminLearningContentTableRow[] {
  const mapped: AdminLearningContentTableRow[] = rows.map((row, idx) => ({
    ...row,
    _localId: row.id ?? `row-${idx}`,
    order_index: idx,
  }));
  const target = Math.max(padTarget, mapped.length + padBoostAfterData);
  for (let i = mapped.length; i < target; i += 1) {
    mapped.push(emptyRow(workspaceId, gradeNumber, subCategory, i));
  }
  return mapped;
}

function normalizeRows(
  rows: AdminLearningContentTableRow[],
  padTarget: number = MIN_VISIBLE_ROW_COUNT,
  padBoostAfterData = 25
): AdminLearningContentTableRow[] {
  const normalized = rows.map((row, idx) => ({ ...row, order_index: idx }));
  const workspaceId = normalized[0]?.workspace_id ?? null;
  const gradeNumber = (normalized[0]?.grade_number ?? 1) as 1 | 2 | 3 | 4 | 5 | 6;
  const subCategory = (normalized[0]?.sub_category ?? 'common') as AdminLearningContentSubCategory;
  const target = Math.max(padTarget, normalized.length + padBoostAfterData);
  for (let i = normalized.length; i < target; i += 1) {
    normalized.push(emptyRow(workspaceId, gradeNumber, subCategory, i));
  }
  return normalized;
}

function hasAnyCellData(
  row: Pick<AdminLearningContentRowUpsert, ColumnKey>,
  columns: Array<{ key: ColumnKey }>
): boolean {
  return columns.some(({ key }) => (row[key] ?? '').trim().length > 0);
}

function serializeCrossCurricularField(textRaw: string, noteRaw: string): string {
  const text = (textRaw ?? '').trim();
  const note = (noteRaw ?? '').trim();
  if (!text && !note) return '';
  if (!note) return text;
  return JSON.stringify({ text, note });
}

function parseCrossCurricularField(raw: string): { text: string; note: string } {
  return getCrossTopicAndNoteForDisplay(raw ?? '');
}

function serializeMemoLikeField(textRaw: string, noteRaw: string): string {
  const text = textRaw ?? '';
  const note = noteRaw ?? '';
  if (!text.trim() && !note.trim()) return '';
  if (!note.trim()) return text;
  return JSON.stringify({ text, note });
}

function parseMemoLikeField(raw: string): { text: string; note: string } {
  const { text, note } = parseMemoFieldStorageDeep(raw ?? '');
  return { text, note };
}

const EVALUATION_STATUS_OPTIONS = [
  { value: '1', label: '평가계획', tone: 'plan' as const },
  { value: '2', label: '평가중', tone: 'progress' as const },
  { value: '3', label: '평가완료', tone: 'done' as const },
];

function formatEvaluationStatusForDisplay(raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return '';
  const hit = EVALUATION_STATUS_OPTIONS.find((opt) => opt.value === v);
  return hit ? hit.label : v;
}

function splitPageCellByDelimiter(pageCellRaw: string, delimiter: string): { page: string; assistant: string } | null {
  const d = delimiter ?? '';
  if (!d.length) return null;
  const s = String(pageCellRaw ?? '');
  const idx = s.indexOf(d);
  if (idx < 0) return null;
  const page = s.slice(0, idx).trimEnd();
  const assistant = s.slice(idx);
  return { page, assistant };
}

/** 잘라내기 후 붙여넣기 시 원본 삭제 여부 판별용(줄바꿈 정규화) */
function normalizeTsvForCutCompare(text: string): string {
  return text
    .replace(/^\uFEFF/, '')
    .split(/\r\n|\n|\r/)
    .filter((line) => line.length > 0)
    .join('\n');
}

/**
 * Excel TSV 파서:
 * - 따옴표(")로 감싼 셀 내부의 \n/\r\n 을 같은 셀 값으로 유지
 * - 이스케이프된 따옴표("")를 "로 복원
 * - 필드 구분자는 \t, 레코드 구분자는 줄바꿈
 */
function parseExcelTsv(raw: string): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const text = raw.replace(/^\uFEFF/, '');
  const pushCell = () => {
    row.push(cell);
    cell = '';
  };
  const pushRow = () => {
    pushCell();
    if (row.some((v) => v.length > 0)) out.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i] ?? '';
    const next = text[i + 1] ?? '';
    if (ch === '"') {
      /** CSV/TSV 규칙: 따옴표는 셀 시작에서만 quote 시작으로 본다. */
      const atCellStart = cell.length === 0;
      if (!inQuotes && !atCellStart) {
        cell += ch;
        continue;
      }
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && ch === '\t') {
      pushCell();
      continue;
    }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && next === '\n') i += 1;
      pushRow();
      continue;
    }
    cell += ch;
  }
  if (cell.length > 0 || row.length > 0) pushRow();
  return out;
}

function comparableRows(
  rows: AdminLearningContentTableRow[],
  columns: Array<{ key: ColumnKey }>
): string {
  return JSON.stringify(
    rows.map((row, idx) => ({
      id: row.id ?? null,
      order_index: idx,
      ...Object.fromEntries(columns.map(({ key }) => [key, row[key] ?? ''])),
    }))
  );
}

const AdminLearningContentTable: React.FC<AdminLearningContentTableProps> = ({
  workspaceId,
  gradeNumber,
  subCategory,
  templateName,
  toolbarTitle,
  rows,
  loading = false,
  saving = false,
  onSave,
  showInlineActionButtons = true,
  onBindActions,
  onActionStateChange,
  readOnly = false,
  slimPublisherFilterPadding = false,
  omitEmptyRowsOnSave = true,
  showProgramRecordColumns = false,
}) => {
  const columns = useMemo(
    () =>
      showProgramRecordColumns
        ? [...BASE_COLUMNS, ...PROGRAM_RECORD_COLUMNS]
        : BASE_COLUMNS,
    [showProgramRecordColumns]
  );
  const modalEditColumnKeySet = useMemo(
    () =>
      new Set<ColumnKey>([
        'record_cross_curricular_text',
        'record_memo_text',
        'record_evaluation_text',
      ]),
    []
  );
  /** 슬림 모드: 편집 시에는 맨 아래 한 줄 여유(신규 입력). 읽기 전용(출판사 모아보기 등)에서는 빈 줄 없음. */
  const padBoostAfterData = slimPublisherFilterPadding ? (readOnly ? 0 : 1) : 25;
  const padTargetRows = useMemo(() => {
    if (!slimPublisherFilterPadding) return MIN_VISIBLE_ROW_COUNT;
    if (readOnly) return Math.max(rows.length, 1);
    return Math.max(rows.length + 1, 1);
  }, [readOnly, slimPublisherFilterPadding, rows.length]);

  const [draftRows, setDraftRows] = useState<AdminLearningContentTableRow[]>([]);
  const [anchorCell, setAnchorCell] = useState<{ row: number; col: number } | null>(null);
  const [focusCell, setFocusCell] = useState<{ row: number; col: number } | null>(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; col: number; value: string } | null>(null);
  const [undoStack, setUndoStack] = useState<AdminLearningContentTableRow[][]>([]);
  const [redoStack, setRedoStack] = useState<AdminLearningContentTableRow[][]>([]);
  const [busySave, setBusySave] = useState(false);
  const [pageSplitModalOpen, setPageSplitModalOpen] = useState(false);
  const [pageSplitDelimiterInput, setPageSplitDelimiterInput] = useState('');
  const [lessonCountModalOpen, setLessonCountModalOpen] = useState(false);
  const [lessonCountDelimiterInput, setLessonCountDelimiterInput] = useState('');
  const [recordCellModal, setRecordCellModal] = useState<{
    row: number;
    col: number;
    key: ColumnKey;
    text: string;
    note: string;
    expanded: boolean;
  } | null>(null);
  const [crossCatalogPickerOpen, setCrossCatalogPickerOpen] = useState(false);
  const crossCatalogPickerOpenRef = useRef(false);
  crossCatalogPickerOpenRef.current = crossCatalogPickerOpen;
  const [copiedSourceRect, setCopiedSourceRect] = useState<{
    minRow: number;
    maxRow: number;
    minCol: number;
    maxCol: number;
  } | null>(null);
  /** 잘라내기: 붙여넣기 성공 시에만 원본 영역을 비움(클립보드 TSV와 일치할 때) */
  const pendingCutRef = useRef<{
    rect: { minRow: number; maxRow: number; minCol: number; maxCol: number };
    tsvNormalized: string;
  } | null>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const cellAnchorByKeyRef = useRef<Map<string, HTMLTableCellElement>>(new Map());
  const recordPopupRef = useRef<HTMLDivElement | null>(null);
  const recordTextInputRef = useRef<HTMLInputElement | null>(null);
  const recordCrossNoteInputRef = useRef<HTMLInputElement | null>(null);
  const recordNoteInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [recordFloatingStyle, setRecordFloatingStyle] = useState<React.CSSProperties | null>(null);
  const anchorRef = useRef<{ row: number; col: number } | null>(null);
  const focusRef = useRef<{ row: number; col: number } | null>(null);
  const isDraggingSelectionRef = useRef(false);
  const didDragSelectionRef = useRef(false);
  /** 드래그 선택 중 자동 스크롤 + elementFromPoint 용 최신 포인터 */
  const lastDragPointerRef = useRef({ x: 0, y: 0 });
  const keyboardActiveRef = useRef(false);
  const undoActionRef = useRef<() => void>(() => undefined);
  const redoActionRef = useRef<() => void>(() => undefined);
  const saveActionRef = useRef<() => Promise<void>>(async () => undefined);
  const pageSplitDelimiterInputRef = useRef<HTMLInputElement>(null);
  const lessonCountDelimiterInputRef = useRef<HTMLInputElement>(null);
  /** `rows` 반영 직후 draft와 함께 갱신 — 로드 완료 직전 프레임의 거짓 dirty 방지 */
  const baselineComparableRef = useRef('');
  const [rowsHydrated, setRowsHydrated] = useState(false);

  React.useEffect(() => {
    if (loading) {
      setRowsHydrated(false);
      return;
    }
    const next = toLocalRows(rows, workspaceId, gradeNumber, subCategory, padTargetRows, padBoostAfterData);
    baselineComparableRef.current = comparableRows(normalizeRows(next, padTargetRows, padBoostAfterData), columns);
    setDraftRows(next);
    setRowsHydrated(true);
    const initial = rows.length ? { row: 0, col: 0 } : null;
    setAnchorCell(initial);
    setFocusCell(initial);
    anchorRef.current = initial;
    focusRef.current = initial;
    setUndoStack([]);
    setRedoStack([]);
    setEditingCell(null);
    setRecordCellModal(null);
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
  }, [gradeNumber, loading, rows, workspaceId, subCategory, templateName, padTargetRows, padBoostAfterData, columns]);

  const selectedRect = useMemo(() => {
    if (!anchorCell || !focusCell) return null;
    return {
      minRow: Math.min(anchorCell.row, focusCell.row),
      maxRow: Math.max(anchorCell.row, focusCell.row),
      minCol: Math.min(anchorCell.col, focusCell.col),
      maxCol: Math.max(anchorCell.col, focusCell.col),
    };
  }, [anchorCell, focusCell]);
  const selectedCellCount = useMemo(() => {
    if (!selectedRect) return 0;
    return (selectedRect.maxRow - selectedRect.minRow + 1) * (selectedRect.maxCol - selectedRect.minCol + 1);
  }, [selectedRect]);
  const selectedRowsSorted = useMemo(() => {
    if (!selectedRect) return [];
    const out: number[] = [];
    for (let r = selectedRect.minRow; r <= selectedRect.maxRow; r += 1) out.push(r);
    return out;
  }, [selectedRect]);
  const hasSelection = selectedCellCount > 0;
  const hasSelectedRows = selectedRowsSorted.length > 0;
  const effectiveDraftRows = useMemo(() => {
    if (!editingCell) return draftRows;
    const key = columns[editingCell.col]?.key;
    if (!key || !draftRows[editingCell.row]) return draftRows;
    return draftRows.map((row, idx) =>
      idx === editingCell.row ? { ...row, [key]: editingCell.value } : row
    );
  }, [columns, draftRows, editingCell]);
  const isDirty = useMemo(() => {
    if (readOnly || !templateName.trim() || loading || saving || !rowsHydrated) return false;
    return (
      comparableRows(normalizeRows(effectiveDraftRows, padTargetRows, padBoostAfterData), columns) !==
      baselineComparableRef.current
    );
  }, [
    effectiveDraftRows,
    loading,
    padBoostAfterData,
    padTargetRows,
    readOnly,
    rowsHydrated,
    saving,
    templateName,
    columns,
  ]);

  const pushUndoSnapshot = (current: AdminLearningContentTableRow[]) => {
    setUndoStack((prev) => [...prev, current.map((row) => ({ ...row }))]);
    setRedoStack([]);
  };

  const updateRows = (updater: (prev: AdminLearningContentTableRow[]) => AdminLearningContentTableRow[]) => {
    setDraftRows((prev) => {
      const prevClone = prev.map((row) => ({ ...row }));
      pushUndoSnapshot(prevClone);
      return normalizeRows(updater(prevClone), padTargetRows, padBoostAfterData);
    });
  };

  const setSelection = (nextAnchor: { row: number; col: number }, nextFocus: { row: number; col: number }) => {
    setAnchorCell(nextAnchor);
    setFocusCell(nextFocus);
    anchorRef.current = nextAnchor;
    focusRef.current = nextFocus;
  };

  const isSelectedCell = (row: number, col: number): boolean => {
    if (!selectedRect) return false;
    return (
      row >= selectedRect.minRow &&
      row <= selectedRect.maxRow &&
      col >= selectedRect.minCol &&
      col <= selectedRect.maxCol
    );
  };

  const isCopiedSourceCell = (row: number, col: number): boolean => {
    if (!copiedSourceRect) return false;
    return (
      row >= copiedSourceRect.minRow &&
      row <= copiedSourceRect.maxRow &&
      col >= copiedSourceRect.minCol &&
      col <= copiedSourceRect.maxCol
    );
  };

  const startEditCell = (row: number, col: number, seed?: string) => {
    if (readOnly) return;
    const key = columns[col]?.key;
    if (!key) return;
    setEditingCell({ row, col, value: seed ?? draftRows[row]?.[key] ?? '' });
  };

  const commitEditingCell = () => {
    if (!editingCell) return;
    const key = columns[editingCell.col]?.key;
    if (!key) {
      setEditingCell(null);
      return;
    }
    setDraftRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const before = next[editingCell.row]?.[key] ?? '';
      if (before === editingCell.value) return prev;
      pushUndoSnapshot(prev.map((row) => ({ ...row })));
      if (next[editingCell.row]) next[editingCell.row][key] = editingCell.value;
      return next;
    });
    setEditingCell(null);
    setRecordCellModal(null);
  };

  const handleCellChange = (value: string) => {
    setEditingCell((prev) => (prev ? { ...prev, value } : prev));
  };
  const openRecordCellModal = (row: number, col: number) => {
    if (readOnly) return;
    const key = columns[col]?.key;
    if (!key || !modalEditColumnKeySet.has(key)) return;
    const raw = draftRows[row]?.[key] ?? '';
    const parsed =
      key === 'record_cross_curricular_text'
        ? parseCrossCurricularField(raw)
        : parseMemoLikeField(raw);
    setRecordCellModal({
      row,
      col,
      key,
      text: parsed.text ?? '',
      note: parsed.note ?? '',
      expanded: false,
    });
  };
  const commitRecordCellModal = () => {
    if (!recordCellModal) return;
    const { row, key, text, note } = recordCellModal;
    const value =
      key === 'record_cross_curricular_text'
        ? serializeCrossCurricularField(text, note)
        : serializeMemoLikeField(text, note);
    setDraftRows((prev) => {
      const next = prev.map((v) => ({ ...v }));
      const before = next[row]?.[key] ?? '';
      if (before === value) return prev;
      pushUndoSnapshot(prev.map((v) => ({ ...v })));
      if (next[row]) next[row][key] = value;
      return next;
    });
    setCrossCatalogPickerOpen(false);
    setRecordCellModal(null);
  };
  const closeRecordCellModal = () => {
    setCrossCatalogPickerOpen(false);
    setRecordCellModal(null);
  };
  const recordModalAnchorKey = recordCellModal ? `${recordCellModal.row}\t${recordCellModal.col}` : null;
  const isCrossRecordModal = recordCellModal?.key === 'record_cross_curricular_text';
  const isEvaluationRecordModal = recordCellModal?.key === 'record_evaluation_text';

  const recomputeRecordFloatingStyle = React.useCallback(() => {
    if (!recordModalAnchorKey) {
      setRecordFloatingStyle(null);
      return;
    }
    const cellEl = cellAnchorByKeyRef.current.get(recordModalAnchorKey);
    if (!cellEl) {
      setRecordFloatingStyle(null);
      return;
    }
    const rect = cellEl.getBoundingClientRect();
    const hostRect = tableWrapRef.current?.getBoundingClientRect();
    const pad = 8;
    const gap = 2;
    const boundsLeft = Math.max(pad, Math.floor(hostRect?.left ?? 0) + 2);
    const boundsRight = Math.min(window.innerWidth - pad, Math.ceil(hostRect?.right ?? window.innerWidth) - 2);
    const boundsTop = Math.max(pad, Math.floor(hostRect?.top ?? 0) + 2);
    const boundsBottom = Math.min(window.innerHeight - pad, Math.ceil(hostRect?.bottom ?? window.innerHeight) - 2);
    const maxUsableWidth = Math.max(220, boundsRight - boundsLeft);
    const spaceBelow = Math.max(0, boundsBottom - (rect.bottom + gap));
    const spaceAbove = Math.max(0, rect.top - gap - boundsTop);
    const placeBelow = spaceBelow >= spaceAbove;
    const availableHeight = Math.max(120, placeBelow ? spaceBelow : spaceAbove);
    const top = Math.min(Math.max(boundsTop, rect.bottom + gap), boundsBottom - 2);
    const desiredBottom = window.innerHeight - (rect.top - gap);
    const minBottom = window.innerHeight - boundsBottom;
    const maxBottom = window.innerHeight - boundsTop;
    const bottom = Math.min(Math.max(minBottom, desiredBottom), maxBottom);
    const verticalStyle: Pick<React.CSSProperties, 'top' | 'bottom'> = placeBelow
      ? { top, bottom: 'auto' }
      : { top: 'auto', bottom };
    const desiredWidth = isCrossRecordModal ? 760 : isEvaluationRecordModal ? 620 : 420;
    const measuredWidth = recordPopupRef.current?.offsetWidth ?? desiredWidth;
    const width = Math.min(Math.max(220, measuredWidth), maxUsableWidth);
    const maxLeft = Math.max(boundsLeft, boundsRight - width);
    const leftBase = isCrossRecordModal ? rect.left : rect.right - width;
    const left = Math.min(Math.max(boundsLeft, leftBase), maxLeft);
    setRecordFloatingStyle({
      position: 'fixed',
      ...verticalStyle,
      left,
      width,
      maxWidth: maxUsableWidth,
      maxHeight: availableHeight,
      zIndex: 10060,
    });
  }, [isCrossRecordModal, isEvaluationRecordModal, recordModalAnchorKey]);
  const displayTextByField = (key: ColumnKey, raw: string): string => {
    if (key === 'record_cross_curricular_text') return parseCrossCurricularField(raw).text;
    if (key === 'record_memo_text') return parseMemoLikeField(raw).text;
    if (key === 'record_evaluation_text') return formatEvaluationStatusForDisplay(parseMemoLikeField(raw).text);
    return raw;
  };

  const handleAddRow = () => {
    updateRows((prev) => {
      const insertAt = selectedRowsSorted.length ? selectedRowsSorted[selectedRowsSorted.length - 1] + 1 : prev.length;
      const row = emptyRow(workspaceId, gradeNumber, subCategory, insertAt);
      return [...prev.slice(0, insertAt), row, ...prev.slice(insertAt)];
    });
    const row = selectedRowsSorted.length ? selectedRowsSorted[selectedRowsSorted.length - 1] + 1 : draftRows.length;
    const safeRow = Math.max(0, Math.min(row, draftRows.length));
    const next = { row: safeRow, col: focusCell?.col ?? 0 };
    setSelection(next, next);
  };

  const handleDeleteRows = () => {
    if (!hasSelectedRows) return;
    updateRows((prev) => prev.filter((_, idx) => !selectedRowsSorted.includes(idx)));
    const nextRow = Math.max(0, (selectedRowsSorted[0] ?? 0) - 1);
    const next = { row: nextRow, col: focusCell?.col ?? 0 };
    setSelection(next, next);
    setEditingCell(null);
    setRecordCellModal(null);
  };

  const handleClearData = () => {
    if (!selectedRect) return;
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
    updateRows((prev) =>
      prev.map((row, idx) =>
        idx >= selectedRect.minRow && idx <= selectedRect.maxRow
          ? {
              ...row,
              ...Object.fromEntries(
                columns.filter((_, colIndex) => colIndex >= selectedRect.minCol && colIndex <= selectedRect.maxCol).map(
                  (column) => [column.key, '']
                )
              ),
            }
          : row
      )
    );
  };

  const handleMergeRows = () => {
    if (selectedRowsSorted.length < 2) return;
    const mergeTextValues = (values: string[]) => {
      const cleaned = values.map((v) => (v ?? '').trim()).filter(Boolean);
      if (!cleaned.length) return '';
      const unique = Array.from(new Set(cleaned));
      return unique.join(', ');
    };
    const mergePageRangeValues = (values: string[]) => {
      const cleaned = values.map((v) => (v ?? '').trim()).filter(Boolean);
      if (!cleaned.length) return '';
      const parsed = cleaned
        .map((v) => {
          const m = v.match(/(\d+)\s*(?:-\s*(\d+))?/);
          if (!m) return null;
          const start = Number(m[1]);
          const end = Number(m[2] ?? m[1]);
          if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
          return { start, end };
        })
        .filter((v): v is { start: number; end: number } => !!v);
      if (!parsed.length) return mergeTextValues(cleaned);
      const start = parsed[0].start;
      const end = parsed[parsed.length - 1].end;
      return start === end ? String(start) : `${start}-${end}`;
    };
    updateRows((prev) => {
      const topIndex = selectedRowsSorted[0];
      const merged = { ...prev[topIndex] };
      merged.learning_text = mergeTextValues(selectedRowsSorted.map((idx) => prev[idx]?.learning_text ?? ''));
      merged.material_text = mergeTextValues(selectedRowsSorted.map((idx) => prev[idx]?.material_text ?? ''));
      merged.page_text = mergePageRangeValues(selectedRowsSorted.map((idx) => prev[idx]?.page_text ?? ''));
      merged.assistant_page_text = mergePageRangeValues(
        selectedRowsSorted.map((idx) => prev[idx]?.assistant_page_text ?? '')
      );
      const removeSet = new Set(selectedRowsSorted.slice(1));
      const next = prev.filter((_, idx) => !removeSet.has(idx));
      next[topIndex] = merged;
      return next;
    });
    const row = selectedRowsSorted[0];
    const next = { row, col: focusCell?.col ?? 0 };
    setSelection(next, next);
  };

  const handleDuplicateRows = () => {
    if (!selectedRowsSorted.length) return;
    updateRows((prev) => {
      const out: AdminLearningContentTableRow[] = [];
      prev.forEach((row, idx) => {
        out.push(row);
        if (selectedRowsSorted.includes(idx)) {
          out.push({ ...row, id: undefined, _localId: `dup-${row._localId}-${idx}-${Date.now()}` });
        }
      });
      return out;
    });
  };

  const handleMoveRows = (direction: -1 | 1) => {
    if (!selectedRowsSorted.length) return;
    updateRows((prev) => {
      const next = [...prev];
      if (direction < 0) {
        for (const idx of selectedRowsSorted) {
          if (idx <= 0 || selectedRowsSorted.includes(idx - 1)) continue;
          const temp = next[idx - 1];
          next[idx - 1] = next[idx];
          next[idx] = temp;
        }
      } else {
        for (let p = selectedRowsSorted.length - 1; p >= 0; p -= 1) {
          const idx = selectedRowsSorted[p];
          if (idx >= next.length - 1 || selectedRowsSorted.includes(idx + 1)) continue;
          const temp = next[idx + 1];
          next[idx + 1] = next[idx];
          next[idx] = temp;
        }
      }
      return next;
    });
  };

  const commitLessonCount = (delimiter: string) => {
    updateRows((prev) => {
      const assignments = computeLessonCountAssignments(
        prev.map((row) => row.unit_text ?? ''),
        delimiter,
        prev.map((row) => row.publisher ?? '')
      );
      const next = prev.map((row, idx) => {
        const assignment = assignments[idx];
        if (!assignment) return { ...row };
        return {
          ...row,
          target_lesson_text: String(assignment.current),
          total_lesson_text: String(assignment.total),
        };
      });
      return next;
    });
    saveLessonCountUnitDelimiter(delimiter);
    setLessonCountModalOpen(false);
  };

  const openLessonCountModal = () => {
    setLessonCountDelimiterInput(loadLessonCountUnitDelimiter());
    setLessonCountModalOpen(true);
  };

  const handleAutoNumberSeq = () => {
    updateRows((prev) => {
      const counters = new Map<string, number>();
      return prev.map((row) => {
        const publisher = (row.publisher ?? '').trim();
        if (!publisher) {
          if (!(row.seq_text ?? '').length) return row;
          return { ...row, seq_text: '' };
        }
        const next = (counters.get(publisher) ?? 0) + 1;
        counters.set(publisher, next);
        const nextSeq = String(next);
        if ((row.seq_text ?? '') === nextSeq) return row;
        return { ...row, seq_text: nextSeq };
      });
    });
  };

  const copyRowsAsTsv = async (cut: boolean) => {
    if (!selectedRect) return;
    const lines: string[] = [];
    for (let r = selectedRect.minRow; r <= selectedRect.maxRow; r += 1) {
      const row = columns.map(({ key }, colIndex) => {
        if (colIndex < selectedRect.minCol || colIndex > selectedRect.maxCol) return null;
        return (draftRows[r]?.[key] ?? '').replace(/\t/g, ' ');
      }).filter((v): v is string => v != null);
      lines.push(row.join('\t'));
    }
    const body = lines.join('\n');
    await navigator.clipboard.writeText(body);
    const rect = { ...selectedRect };
    setCopiedSourceRect(rect);
    if (cut) {
      pendingCutRef.current = { rect, tsvNormalized: normalizeTsvForCutCompare(body) };
    } else {
      pendingCutRef.current = null;
    }
  };

  const handlePaste = async () => {
    if (!focusCell && !selectedRect) return;
    const raw = await navigator.clipboard.readText();
    if (!raw.trim()) return;
    const clipNorm = normalizeTsvForCutCompare(raw);
    const pendingCut = pendingCutRef.current;
    const cutRect = pendingCut && clipNorm === pendingCut.tsvNormalized ? pendingCut.rect : null;
    let grid = parseExcelTsv(raw);
    /**
     * 일부 Excel/클립보드 환경은 단일 셀(줄바꿈 포함)을
     * 탭 없는 여러 줄 텍스트로 내려준다. 성취기준 열 시작 붙여넣기에서는
     * 이를 하나의 셀 값(\n 유지)으로 복원한다.
     */
    const startCol = selectedRect ? selectedRect.minCol : focusCell?.col;
    const startColumnKey = typeof startCol === 'number' ? columns[startCol]?.key : undefined;
    if (
      startColumnKey === 'achievement_text' &&
      !raw.includes('\t') &&
      grid.length > 1 &&
      grid.every((r) => r.length === 1)
    ) {
      grid = [[grid.map((r) => r[0] ?? '').join('\n')]];
    }
    if (!grid.length) return;

    const clearCutRectInRows = (
      rowsIn: AdminLearningContentTableRow[],
      rect: { minRow: number; maxRow: number; minCol: number; maxCol: number },
      skipCell?: (row: number, colIndex: number) => boolean
    ) =>
      rowsIn.map((row, idx) => {
        if (idx < rect.minRow || idx > rect.maxRow) return row;
        const nextRow = { ...row };
        let touched = false;
        for (let colIndex = rect.minCol; colIndex <= rect.maxCol; colIndex += 1) {
          if (skipCell?.(idx, colIndex)) continue;
          const column = columns[colIndex];
          if (!column) continue;
          if ((nextRow[column.key] ?? '') !== '') {
            nextRow[column.key] = '';
            touched = true;
          }
        }
        return touched ? nextRow : row;
      });

    if (selectedRect && grid.length === 1 && grid[0].length === 1) {
      const fillValue = grid[0][0] ?? '';
      updateRows((prev) => {
        let next = prev.map((row, rowIndex) => {
          if (rowIndex < selectedRect.minRow || rowIndex > selectedRect.maxRow) return row;
          let changed = false;
          const nextRow = { ...row };
          for (let colIndex = selectedRect.minCol; colIndex <= selectedRect.maxCol; colIndex += 1) {
            const column = columns[colIndex];
            if (!column) continue;
            if ((nextRow[column.key] ?? '') === fillValue) continue;
            nextRow[column.key] = fillValue;
            changed = true;
          }
          return changed ? nextRow : row;
        });
        if (cutRect) {
          next = clearCutRectInRows(next, cutRect, (r, c) => {
            if (r < selectedRect.minRow || r > selectedRect.maxRow) return false;
            if (c < selectedRect.minCol || c > selectedRect.maxCol) return false;
            return true;
          });
        }
        return next;
      });
      pendingCutRef.current = null;
      setCopiedSourceRect(null);
      return;
    }

    if (!focusCell) return;
    updateRows((prev) => {
      const out = [...prev];
      const neededRows = focusCell.row + grid.length;
      while (out.length < neededRows) {
        out.push(emptyRow(workspaceId, gradeNumber, subCategory, out.length));
      }
      grid.forEach((cols, r) => {
        cols.forEach((value, c) => {
          const colIndex = focusCell.col + c;
          const column = columns[colIndex];
          if (!column) return;
          out[focusCell.row + r][column.key] = value;
        });
      });
      if (!cutRect) return out;
      return clearCutRectInRows(out, cutRect, (r, c) => {
        const dr = r - focusCell.row;
        if (dr < 0 || dr >= grid.length) return false;
        const rowLen = grid[dr]?.length ?? 0;
        const dc = c - focusCell.col;
        return dc >= 0 && dc < rowLen;
      });
    });
    pendingCutRef.current = null;
    setCopiedSourceRect(null);
  };

  const clearClipboardSelectionState = () => {
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText('').catch(() => undefined);
    }
  };

  const handleUndo = () => {
    setEditingCell(null);
    setRecordCellModal(null);
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
    setUndoStack((prev) => {
      if (!prev.length) return prev;
      const snapshot = prev[prev.length - 1];
      setRedoStack((redoPrev) => [...redoPrev, draftRows.map((row) => ({ ...row }))]);
      setDraftRows(snapshot.map((row, idx) => ({ ...row, order_index: idx })));
      return prev.slice(0, -1);
    });
  };

  const handleRedo = () => {
    setEditingCell(null);
    setCopiedSourceRect(null);
    pendingCutRef.current = null;
    setRedoStack((prev) => {
      if (!prev.length) return prev;
      const snapshot = prev[prev.length - 1];
      setUndoStack((undoPrev) => [...undoPrev, draftRows.map((row) => ({ ...row }))]);
      setDraftRows(snapshot.map((row, idx) => ({ ...row, order_index: idx })));
      return prev.slice(0, -1);
    });
  };

  const handleSave = async () => {
    if (readOnly) return;
    commitEditingCell();
    setBusySave(true);
    try {
      const mapped = draftRows.map(({ _localId: _ignored, ...row }, idx) => ({
        ...row,
        id: row.id,
        order_index: idx,
        workspace_id: workspaceId,
        grade_number: gradeNumber,
        sub_category: subCategory,
        template_name: templateName.trim(),
      }));
      let toSave: typeof mapped;
      if (omitEmptyRowsOnSave) {
        toSave = mapped.filter((row) => hasAnyCellData(row, columns));
      } else {
        let lastData = -1;
        for (let i = 0; i < mapped.length; i += 1) {
          if (hasAnyCellData(mapped[i], columns)) lastData = i;
        }
        toSave = lastData < 0 ? [] : mapped.slice(0, lastData + 1);
      }
      await onSave(toSave);
      setUndoStack([]);
      setRedoStack([]);
    } finally {
      setBusySave(false);
    }
  };

  const openPageAssistantSplitModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    commitEditingCell();
    setPageSplitDelimiterInput('');
    setPageSplitModalOpen(true);
  };

  const commitPageAssistantSplit = () => {
    const delimiter = pageSplitDelimiterInput;
    if (!delimiter.length) return;
    commitEditingCell();
    updateRows((prev) =>
      prev.map((row) => {
        const split = splitPageCellByDelimiter(row.page_text ?? '', delimiter);
        if (!split) return row;
        return {
          ...row,
          page_text: split.page,
          assistant_page_text: split.assistant,
        };
      })
    );
    setPageSplitModalOpen(false);
  };

  React.useEffect(() => {
    if (!onBindActions) return;
    onBindActions({
      undo: () => undoActionRef.current(),
      redo: () => redoActionRef.current(),
      save: () => saveActionRef.current(),
    });
    return () => onBindActions(null);
  }, [onBindActions]);

  React.useEffect(() => {
    onActionStateChange?.({
      canUndo: readOnly ? false : undoStack.length > 0,
      canRedo: readOnly ? false : redoStack.length > 0,
      canSave: readOnly ? false : !loading && !saving && !busySave && isDirty,
    });
  }, [busySave, isDirty, loading, onActionStateChange, readOnly, redoStack.length, saving, undoStack.length]);

  React.useEffect(() => {
    if (!isDraggingSelection) return;

    const applySelectionFromClientPoint = (clientX: number, clientY: number) => {
      lastDragPointerRef.current = { x: clientX, y: clientY };
      if (!isDraggingSelectionRef.current || !anchorRef.current) return;
      const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const cellEl = target?.closest?.('td[data-admin-cell="1"]') as HTMLTableCellElement | null;
      if (!cellEl) return;
      const row = Number(cellEl.dataset.rowIndex ?? '');
      const col = Number(cellEl.dataset.colIndex ?? '');
      if (!Number.isInteger(row) || !Number.isInteger(col)) return;
      const prevFocus = focusRef.current;
      if (!prevFocus || prevFocus.row !== row || prevFocus.col !== col) {
        didDragSelectionRef.current = true;
      }
      setSelection(anchorRef.current, { row, col });
    };

    const scrollWrapTowardPointer = (): boolean => {
      const wrap = tableWrapRef.current;
      if (!wrap) return false;
      const { x, y } = lastDragPointerRef.current;
      const r = wrap.getBoundingClientRect();
      const margin = 44;
      const step = 36;
      let scrolled = false;
      if (y < r.top + margin) {
        wrap.scrollTop = Math.max(0, wrap.scrollTop - step);
        scrolled = true;
      } else if (y > r.bottom - margin) {
        const maxY = Math.max(0, wrap.scrollHeight - wrap.clientHeight);
        wrap.scrollTop = Math.min(maxY, wrap.scrollTop + step);
        scrolled = true;
      }
      if (x < r.left + margin) {
        wrap.scrollLeft = Math.max(0, wrap.scrollLeft - step);
        scrolled = true;
      } else if (x > r.right - margin) {
        const maxX = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
        wrap.scrollLeft = Math.min(maxX, wrap.scrollLeft + step);
        scrolled = true;
      }
      return scrolled;
    };

    let rafId = 0;
    const tick = () => {
      if (!isDraggingSelectionRef.current) return;
      if (scrollWrapTowardPointer()) {
        const { x, y } = lastDragPointerRef.current;
        applySelectionFromClientPoint(x, y);
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);

    const onMouseMove = (event: MouseEvent) => {
      applySelectionFromClientPoint(event.clientX, event.clientY);
    };
    const onMouseUp = () => {
      setIsDraggingSelection(false);
      isDraggingSelectionRef.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDraggingSelection]);

  React.useEffect(() => {
    if (!pageSplitModalOpen) return;
    const id = window.setTimeout(() => {
      pageSplitDelimiterInputRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(id);
  }, [pageSplitModalOpen]);

  React.useLayoutEffect(() => {
    recomputeRecordFloatingStyle();
  }, [recomputeRecordFloatingStyle]);

  React.useLayoutEffect(() => {
    if (!recordCellModal) return;
    const raf = window.requestAnimationFrame(() => recomputeRecordFloatingStyle());
    return () => window.cancelAnimationFrame(raf);
  }, [
    recordCellModal,
    recordCellModal?.text,
    recordCellModal?.note,
    recordCellModal?.expanded,
    recomputeRecordFloatingStyle,
  ]);

  React.useEffect(() => {
    if (!recordCellModal) return;
    const onResize = () => recomputeRecordFloatingStyle();
    const onScroll = () => recomputeRecordFloatingStyle();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [recordCellModal, recomputeRecordFloatingStyle]);

  React.useEffect(() => {
    if (!recordCellModal) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node | null;
      if (!target) return;
      if (recordPopupRef.current?.contains(target)) return;
      if (crossCatalogPickerOpenRef.current) return;
      const targetEl = target instanceof Element ? target : target.parentElement;
      if (
        targetEl?.closest(
          '.cp-cross-curricular-catalog-picker-backdrop, .cp-cross-curricular-catalog-picker-modal'
        )
      ) {
        return;
      }
      closeRecordCellModal();
    };
    const onDocKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        if (crossCatalogPickerOpenRef.current) {
          ev.preventDefault();
          setCrossCatalogPickerOpen(false);
          return;
        }
        ev.preventDefault();
        closeRecordCellModal();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onDocKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onDocKeyDown, true);
    };
  }, [recordCellModal]);

  React.useEffect(() => {
    if (!recordCellModal) return;
    const id = window.setTimeout(() => {
      if (recordCellModal.key === 'record_cross_curricular_text') {
        recordTextInputRef.current?.focus({ preventScroll: true });
        recordTextInputRef.current?.select();
        return;
      }
      if (recordCellModal.key === 'record_evaluation_text') {
        recordNoteInputRef.current?.focus({ preventScroll: true });
        return;
      }
      recordTextInputRef.current?.focus({ preventScroll: true });
      recordTextInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [recordCellModal]);

  const moveFocusByArrow = (dx: number, dy: number, extend: boolean) => {
    if (!focusCell || !draftRows.length) return;
    const next = {
      row: Math.max(0, Math.min(draftRows.length - 1, focusCell.row + dy)),
      col: Math.max(0, Math.min(columns.length - 1, focusCell.col + dx)),
    };
    if (extend && anchorCell) setSelection(anchorCell, next);
    else setSelection(next, next);
  };

  const moveFocusAndStartEdit = (dx: number, dy: number) => {
    if (!focusRef.current || !draftRows.length) return;
    const next = {
      row: Math.max(0, Math.min(draftRows.length - 1, focusRef.current.row + dy)),
      col: Math.max(0, Math.min(columns.length - 1, focusRef.current.col + dx)),
    };
    setSelection(next, next);
    window.requestAnimationFrame(() => {
      if (!readOnly) {
        const key = columns[next.col]?.key;
        if (key && modalEditColumnKeySet.has(key)) {
          openRecordCellModal(next.row, next.col);
        } else {
          startEditCell(next.row, next.col);
        }
      }
    });
  };

  const handleTableKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (editingCell || recordCellModal) return;
    if (readOnly) {
      if (e.key === 'Escape') {
        e.preventDefault();
        clearClipboardSelectionState();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        void copyRowsAsTsv(false);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveFocusByArrow(0, -1, e.shiftKey);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveFocusByArrow(0, 1, e.shiftKey);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        moveFocusByArrow(-1, 0, e.shiftKey);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        moveFocusByArrow(1, 0, e.shiftKey);
        return;
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      clearClipboardSelectionState();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault();
      void copyRowsAsTsv(false);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      void copyRowsAsTsv(true);
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
      e.preventDefault();
      void handlePaste();
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      handleClearData();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocusByArrow(0, -1, e.shiftKey);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocusByArrow(0, 1, e.shiftKey);
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveFocusByArrow(-1, 0, e.shiftKey);
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveFocusByArrow(1, 0, e.shiftKey);
      return;
    }
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      if (focusCell) {
        const key = columns[focusCell.col]?.key;
        if (key && modalEditColumnKeySet.has(key)) {
          openRecordCellModal(focusCell.row, focusCell.col);
        } else {
          startEditCell(focusCell.row, focusCell.col);
        }
      }
      return;
    }
    if (e.key.length === 1 && !e.altKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (focusCell) {
        const key = columns[focusCell.col]?.key;
        if (key && modalEditColumnKeySet.has(key)) {
          openRecordCellModal(focusCell.row, focusCell.col);
        } else {
          startEditCell(focusCell.row, focusCell.col, e.key);
        }
      }
    }
  };

  /* 전역 단축키: 테이블 포커스 시에만 동작. 핸들러는 최신 클로저를 유지해야 하므로 의존성 배열을 넓히지 않음 */
  React.useEffect(() => {
    const onWindowKeyDown = (e: KeyboardEvent) => {
      if (!keyboardActiveRef.current || editingCell || recordCellModal) return;
      if (shouldOuterPageIgnoreKey(e)) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (readOnly) {
        if (mod && key === 'c') {
          e.preventDefault();
          void copyRowsAsTsv(false);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          clearClipboardSelectionState();
          return;
        }
        return;
      }
      if (mod && key === 'c') {
        e.preventDefault();
        void copyRowsAsTsv(false);
        return;
      }
      if (mod && key === 'x') {
        e.preventDefault();
        void copyRowsAsTsv(true);
        return;
      }
      if (mod && key === 'v') {
        e.preventDefault();
        void handlePaste();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        clearClipboardSelectionState();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleClearData();
        return;
      }
      if (e.key === 'Enter' || e.key === 'F2') {
        if (!focusRef.current) return;
        e.preventDefault();
        const key = columns[focusRef.current.col]?.key;
        if (key && modalEditColumnKeySet.has(key)) {
          openRecordCellModal(focusRef.current.row, focusRef.current.col);
        } else {
          startEditCell(focusRef.current.row, focusRef.current.col);
        }
      }
    };
    window.addEventListener('keydown', onWindowKeyDown, true);
    return () => window.removeEventListener('keydown', onWindowKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 키 핸들러는 매 렌더 최신 상태를 캡처
  }, [editingCell, draftRows.length, readOnly, selectedRect, recordCellModal]);

  undoActionRef.current = handleUndo;
  redoActionRef.current = handleRedo;
  saveActionRef.current = handleSave;

  return (
    <section className="cp-admin-learning-table-shell cp-program-progress-table-wrap">
      <div className="cp-program-progress-toolbar">
        <div className="cp-program-progress-toolbar-leading">
          {toolbarTitle ? <span className="cp-admin-learning-toolbar-title">{toolbarTitle}</span> : null}
        </div>
        <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
          {readOnly ? (
            <span className="cp-admin-learning-readonly-toolbar-hint" role="status">
              서비스에서 제공하는 기본 편제입니다. 복사·조회만 가능합니다.
            </span>
          ) : (
            <>
              <div className="cp-program-progress-toolbar-group" role="group" aria-label="행 편집 도구">
                <button type="button" className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus" onClick={handleAddRow}>
                  <span className="cp-btn-label-desktop">수업 추가</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    +
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
                  onClick={handleDeleteRows}
                  disabled={!hasSelectedRows}
                >
                  <span className="cp-btn-label-desktop">수업 삭제</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    -
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region"
                  onClick={handleMergeRows}
                  disabled={selectedRowsSorted.length < 2}
                >
                  <span className="cp-btn-label-desktop">수업 통합</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    통합
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region"
                  onClick={handleDuplicateRows}
                  disabled={!hasSelectedRows}
                >
                  <span className="cp-btn-label-desktop">수업 증배</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    증배
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region"
                  onClick={() => handleMoveRows(-1)}
                  disabled={!hasSelectedRows}
                >
                  <span className="cp-btn-label-desktop">편집내용 올리기</span>
                  <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                    <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                      <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                      <path
                        d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                        fill="currentColor"
                        opacity="0.45"
                      />
                    </svg>
                  </span>
                </button>
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region"
                  onClick={() => handleMoveRows(1)}
                  disabled={!hasSelectedRows}
                >
                  <span className="cp-btn-label-desktop">편집내용 내리기</span>
                  <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
                    <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                      <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                      <path
                        d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                        fill="currentColor"
                        opacity="0.45"
                      />
                    </svg>
                  </span>
                </button>
              </div>
              <div className="cp-program-progress-toolbar-group" role="group" aria-label="차시 자동 카운트">
                <button
                  type="button"
                  className="cp-btn-copy-paste cp-btn-swap-region cp-btn-period-count"
                  onClick={openLessonCountModal}
                >
                  <span className="cp-btn-label-desktop">차시 자동 카운트</span>
                  <span className="cp-btn-label-mobile-icon" aria-hidden>
                    차시 카운트
                  </span>
                </button>
              </div>
            </>
          )}
          <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드·삭제">
            <button type="button" className="cp-btn-copy-paste" onClick={() => void copyRowsAsTsv(false)} disabled={!hasSelection}>
              <span className="cp-btn-label-desktop">복사</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-copy-paste"
              onClick={() => void copyRowsAsTsv(true)}
              disabled={readOnly || !hasSelection}
            >
              <span className="cp-btn-label-desktop">잘라내기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button type="button" className="cp-btn-copy-paste" onClick={() => void handlePaste()} disabled={readOnly || !focusCell}>
              <span className="cp-btn-label-desktop">붙여넣기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            <button type="button" className="cp-btn-copy-paste" onClick={() => handleClearData()} disabled={readOnly || !hasSelection}>
              <span className="cp-btn-label-desktop">데이터 삭제</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
              </span>
            </button>
            {showInlineActionButtons ? (
              <>
            <button type="button" className="cp-btn-copy-paste" onClick={handleUndo} disabled={!undoStack.length}>
              <span className="cp-btn-label-desktop">되돌리기</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M3 7v6h6M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <button type="button" className="cp-btn-copy-paste" onClick={handleRedo} disabled={!redoStack.length}>
              <span className="cp-btn-label-desktop">다시실행</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M21 7v6h-6M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <button
              type="button"
              className="cp-btn-primary"
              onClick={() => void handleSave()}
              disabled={loading || saving || busySave || !isDirty}
            >
              저장
            </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div
        className={`cp-admin-learning-table-wrap${readOnly ? ' cp-admin-learning-table-wrap--readonly' : ''}`}
        ref={tableWrapRef}
        tabIndex={0}
        onFocus={() => {
          keyboardActiveRef.current = true;
        }}
        onKeyDown={handleTableKeyDown}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            keyboardActiveRef.current = false;
            commitEditingCell();
          }
        }}
      >
        <table className="cp-annual-progress-table cp-admin-learning-table">
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} className={column.className} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.className}${column.key === 'page_text' ? ' cp-program-progress-metric-head-th' : ''}`}
                >
                  {column.key === 'seq_text' ? (
                    readOnly ? (
                      <span className="cp-program-progress-record-head-label">순</span>
                    ) : (
                      <button type="button" className="cp-program-progress-record-head-btn" onClick={handleAutoNumberSeq}>
                        순
                      </button>
                    )
                  ) : column.key === 'page_text' ? (
                    readOnly ? (
                      <span className="cp-program-progress-record-head-label">쪽수</span>
                    ) : (
                      <button type="button" className="cp-program-progress-record-head-btn" onClick={openPageAssistantSplitModal}>
                        쪽수
                      </button>
                    )
                  ) : column.key === 'target_lesson_text' ? (
                    <>
                      해당
                      <br />
                      차시
                    </>
                  ) : column.key === 'total_lesson_text' ? (
                    <>
                      전체
                      <br />
                      차시
                    </>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {draftRows.map((row, rowIndex) => (
              <tr key={row._localId}>
                {columns.map((column, colIndex) => {
                  const selected = isSelectedCell(rowIndex, colIndex);
                  const single = selected && selectedCellCount === 1;
                  const copiedSource = isCopiedSourceCell(rowIndex, colIndex);
                  const copiedSourceSingle = !!copiedSourceRect && copiedSource && copiedSourceRect.minRow === copiedSourceRect.maxRow && copiedSourceRect.minCol === copiedSourceRect.maxCol;
                  const copiedSourceMulti = !!copiedSourceRect && copiedSource && !copiedSourceSingle;
                  const editing = editingCell?.row === rowIndex && editingCell?.col === colIndex;
                  const edgeTop = !!selectedRect && rowIndex === selectedRect.minRow;
                  const edgeBottom = !!selectedRect && rowIndex === selectedRect.maxRow;
                  const edgeLeft = !!selectedRect && colIndex === selectedRect.minCol;
                  const edgeRight = !!selectedRect && colIndex === selectedRect.maxCol;
                  const copiedEdgeTop = !!copiedSourceRect && rowIndex === copiedSourceRect.minRow;
                  const copiedEdgeBottom = !!copiedSourceRect && rowIndex === copiedSourceRect.maxRow;
                  const copiedEdgeLeft = !!copiedSourceRect && colIndex === copiedSourceRect.minCol;
                  const copiedEdgeRight = !!copiedSourceRect && colIndex === copiedSourceRect.maxCol;
                  return (
                    <td
                      key={column.key}
                      ref={(el) => {
                        const k = `${rowIndex}\t${colIndex}`;
                        if (el) cellAnchorByKeyRef.current.set(k, el);
                        else cellAnchorByKeyRef.current.delete(k);
                      }}
                      data-admin-cell="1"
                      data-row-index={rowIndex}
                      data-col-index={colIndex}
                      className={[
                        column.className,
                        'cp-program-progress-editable-cell',
                        selected ? (single ? 'cp-base-timetable-cell-selected-single' : 'cp-base-timetable-cell-selected-multi') : '',
                        copiedSource ? 'cp-base-timetable-cell-copied-source' : '',
                        copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
                        copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
                        copiedSourceMulti && copiedEdgeTop ? 'cp-base-timetable-cell-copied-edge-top' : '',
                        copiedSourceMulti && copiedEdgeBottom ? 'cp-base-timetable-cell-copied-edge-bottom' : '',
                        copiedSourceMulti && copiedEdgeLeft ? 'cp-base-timetable-cell-copied-edge-left' : '',
                        copiedSourceMulti && copiedEdgeRight ? 'cp-base-timetable-cell-copied-edge-right' : '',
                        selected && !single && edgeTop ? 'cp-base-timetable-cell-edge-top' : '',
                        selected && !single && edgeBottom ? 'cp-base-timetable-cell-edge-bottom' : '',
                        selected && !single && edgeLeft ? 'cp-base-timetable-cell-edge-left' : '',
                        selected && !single && edgeRight ? 'cp-base-timetable-cell-edge-right' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        tableWrapRef.current?.focus();
                        keyboardActiveRef.current = true;
                        commitEditingCell();
                        setIsDraggingSelection(true);
                        isDraggingSelectionRef.current = true;
                        didDragSelectionRef.current = false;
                        const here = { row: rowIndex, col: colIndex };
                        lastDragPointerRef.current = { x: e.clientX, y: e.clientY };
                        if (e.shiftKey && (anchorRef.current != null || focusRef.current != null)) {
                          const anchor = anchorRef.current ?? focusRef.current ?? here;
                          setSelection(anchor, here);
                        } else {
                          setSelection(here, here);
                        }
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        const here = { row: rowIndex, col: colIndex };
                        setSelection(here, here);
                        const key = columns[colIndex]?.key;
                        if (!readOnly && key && !modalEditColumnKeySet.has(key)) {
                          startEditCell(rowIndex, colIndex);
                        }
                      }}
                      onClick={() => {
                        const key = columns[colIndex]?.key;
                        if (!key || !modalEditColumnKeySet.has(key)) return;
                        if (didDragSelectionRef.current) return;
                        openRecordCellModal(rowIndex, colIndex);
                      }}
                      onMouseEnter={() => {
                        if (!isDraggingSelectionRef.current || !anchorRef.current) return;
                        const prevFocus = focusRef.current;
                        if (!prevFocus || prevFocus.row !== rowIndex || prevFocus.col !== colIndex) {
                          didDragSelectionRef.current = true;
                        }
                        setSelection(anchorRef.current, { row: rowIndex, col: colIndex });
                      }}
                      onMouseMove={() => {
                        if (!isDraggingSelectionRef.current || !anchorRef.current) return;
                        const prevFocus = focusRef.current;
                        if (!prevFocus || prevFocus.row !== rowIndex || prevFocus.col !== colIndex) {
                          didDragSelectionRef.current = true;
                        }
                        setSelection(anchorRef.current, { row: rowIndex, col: colIndex });
                      }}
                    >
                      {editing ? (
                        <input
                          type="text"
                          className="cp-program-progress-cell-input cp-admin-learning-cell-input"
                          value={editingCell.value}
                          autoFocus
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={commitEditingCell}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(0, 1);
                            } else if (e.key === 'Tab') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(e.shiftKey ? -1 : 1, 0);
                            } else if (e.key === 'ArrowLeft') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(-1, 0);
                            } else if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(1, 0);
                            } else if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(0, -1);
                            } else if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              commitEditingCell();
                              moveFocusAndStartEdit(0, 1);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setEditingCell(null);
                            }
                          }}
                        />
                      ) : (
                        <div
                          className={[
                            'cp-admin-learning-cell-text',
                            column.key === 'achievement_text' ? 'cp-admin-learning-cell-text-scrollable' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          tabIndex={column.key === 'achievement_text' ? 0 : -1}
                          onKeyDown={(e) => {
                            if (column.key !== 'achievement_text') return;
                            const el = e.currentTarget;
                            const lineStep = 18;
                            if (e.key === 'ArrowDown') {
                              e.preventDefault();
                              e.stopPropagation();
                              el.scrollTop += lineStep;
                              return;
                            }
                            if (e.key === 'ArrowUp') {
                              e.preventDefault();
                              e.stopPropagation();
                              el.scrollTop -= lineStep;
                              return;
                            }
                            if (e.key === 'PageDown') {
                              e.preventDefault();
                              e.stopPropagation();
                              el.scrollTop += el.clientHeight;
                              return;
                            }
                            if (e.key === 'PageUp') {
                              e.preventDefault();
                              e.stopPropagation();
                              el.scrollTop -= el.clientHeight;
                            }
                          }}
                        >
                          {displayTextByField(column.key, row[column.key] ?? '')}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!draftRows.length ? (
              <tr>
                <td className="cp-admin-learning-empty" colSpan={columns.length}>
                  입력된 데이터가 없습니다. 수업 추가 버튼으로 행을 생성하세요.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {recordCellModal && recordFloatingStyle && isCrossRecordModal
        ? createPortal(
            <div style={recordFloatingStyle} className="cp-program-table-floating-popup">
              <div
                ref={recordPopupRef}
                className="cp-program-cross-popup"
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => ev.stopPropagation()}
              >
                <div className="cp-program-cross-popup-row cp-program-cross-popup-row-horizontal">
                  <div className="cp-program-cross-popup-field">
                    <input
                      ref={recordTextInputRef}
                      type="text"
                      value={recordCellModal.text}
                      placeholder="주제어"
                      onChange={(ev) =>
                        setRecordCellModal((prev) => (prev ? { ...prev, text: ev.target.value } : prev))
                      }
                      onKeyDown={(ev) => {
                        const native = ev.nativeEvent as KeyboardEvent;
                        if (native.isComposing) return;
                        if (ev.key !== 'Enter') return;
                        ev.preventDefault();
                          recordCrossNoteInputRef.current?.focus({ preventScroll: true });
                      }}
                    />
                  </div>
                  <div className="cp-program-cross-popup-field">
                    <input
                      ref={recordCrossNoteInputRef}
                      type="text"
                      value={recordCellModal.note}
                      placeholder="내용"
                      onChange={(ev) =>
                        setRecordCellModal((prev) => (prev ? { ...prev, note: ev.target.value } : prev))
                      }
                      onKeyDown={(ev) => {
                        const native = ev.nativeEvent as KeyboardEvent;
                        if (native.isComposing) return;
                        if (ev.key !== 'Enter' && ev.key !== 'Tab') return;
                        ev.preventDefault();
                        commitRecordCellModal();
                      }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="cp-program-eval-plan-import-btn cp-program-cross-catalog-import-btn"
                  title="관리자 범교과 관리에 등록된 예시문 가져오기"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                  }}
                  onClick={() => setCrossCatalogPickerOpen(true)}
                >
                  범교과 예시문 가져오기
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
      <CrossCurricularCatalogPickerModal
        open={crossCatalogPickerOpen}
        onClose={() => setCrossCatalogPickerOpen(false)}
        onApply={(pick) => {
          setCrossCatalogPickerOpen(false);
          setRecordCellModal((prev) =>
            prev && prev.key === 'record_cross_curricular_text'
              ? { ...prev, text: pick.topicName, note: pick.body }
              : prev
          );
        }}
      />
      {recordCellModal && recordFloatingStyle && !isCrossRecordModal
        ? createPortal(
            <div style={recordFloatingStyle} className="cp-program-table-floating-popup">
              <div
                ref={recordPopupRef}
                className={
                  'cp-program-note-popup cp-program-note-popup--with-head' +
                  (recordCellModal.expanded ? ' cp-program-note-popup-expanded' : '') +
                  ' cp-program-note-popup-right'
                }
                onMouseDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => ev.stopPropagation()}
              >
                <div className="cp-program-note-popup-stack">
                  <div className="cp-program-note-popup-head">
                    <button
                      type="button"
                      className="cp-program-note-popup-expand-btn"
                      aria-label={recordCellModal.expanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      title={recordCellModal.expanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                      onClick={() =>
                        setRecordCellModal((prev) => (prev ? { ...prev, expanded: !prev.expanded } : prev))
                      }
                    >
                      <img
                        src={recordCellModal.expanded ? '/Minimize.svg' : '/Expand.svg'}
                        alt=""
                        aria-hidden
                        className="cp-program-note-popup-expand-icon"
                      />
                    </button>
                  </div>
                  {isEvaluationRecordModal ? (
                    <div className="cp-program-eval-choice-tabs">
                      <div className="cp-annual-progress-tabs cp-program-eval-choice-row-full" role="group">
                        {(() => {
                          const active = !(recordCellModal.text ?? '').trim();
                          return (
                            <button
                              type="button"
                              className={'cp-annual-progress-tab cp-program-eval-tab-clear' + (active ? ' is-active' : '')}
                              onClick={() =>
                                setRecordCellModal((prev) => (prev ? { ...prev, text: '', note: '' } : prev))
                              }
                            >
                              <span className="cp-program-eval-tab-clear-ring" aria-hidden />
                              <span className="cp-program-eval-tab-clear-label">입력없음</span>
                            </button>
                          );
                        })()}
                      </div>
                      <div className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-eval-choice-row-thirds" role="group">
                        {EVALUATION_STATUS_OPTIONS.map((opt) => {
                          const active = (recordCellModal.text ?? '').trim() === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={'cp-annual-progress-tab cp-program-eval-tab-' + opt.tone + (active ? ' is-active' : '')}
                              onClick={() =>
                                setRecordCellModal((prev) => (prev ? { ...prev, text: opt.value } : prev))
                              }
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="cp-program-note-popup-field">
                      <input
                        ref={recordTextInputRef}
                        type="text"
                        value={recordCellModal.text}
                        placeholder="메모"
                        onChange={(ev) =>
                          setRecordCellModal((prev) => (prev ? { ...prev, text: ev.target.value } : prev))
                        }
                        onKeyDown={(ev) => {
                          const native = ev.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (ev.key !== 'Enter') return;
                          ev.preventDefault();
                          recordNoteInputRef.current?.focus({ preventScroll: true });
                        }}
                      />
                    </div>
                  )}
                  <div className="cp-program-note-popup-field">
                    <textarea
                      ref={recordNoteInputRef}
                      rows={7}
                      value={recordCellModal.note}
                      placeholder={isEvaluationRecordModal ? '평가 메모' : '메모 상세'}
                      onChange={(ev) =>
                        setRecordCellModal((prev) => (prev ? { ...prev, note: ev.target.value } : prev))
                      }
                    />
                  </div>
                  <div className="cp-program-note-popup-actions">
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn"
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                      onClick={closeRecordCellModal}
                    >
                      닫기
                    </button>
                    <button
                      type="button"
                      className="cp-program-note-confirm-btn"
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();
                      }}
                      onClick={commitRecordCellModal}
                    >
                      확인
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      <UnitLessonCountDelimiterModal
        open={lessonCountModalOpen}
        delimiter={lessonCountDelimiterInput}
        onDelimiterChange={setLessonCountDelimiterInput}
        onCancel={() => setLessonCountModalOpen(false)}
        onConfirm={() => commitLessonCount(lessonCountDelimiterInput)}
        inputRef={lessonCountDelimiterInputRef}
        publisherScoped
      />
      {pageSplitModalOpen
        ? createPortal(
            <div
              className="cp-modal-backdrop"
              role="presentation"
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget) setPageSplitModalOpen(false);
              }}
            >
              <div
                className="cp-modal cp-program-page-split-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cp-program-page-split-title"
                onMouseDown={(ev) => ev.stopPropagation()}
              >
                <h3 id="cp-program-page-split-title">쪽수 - 보조쪽수 분리</h3>
                <p className="cp-program-page-split-desc">
                  구분 기호를 시작으로 보조쪽수로 분리하여 표시합니다.
                  <br />
                  예: 28-29(30-32) 인 경우 구분 기호 ( 를 넣으면 → 쪽수 28-29, 보조쪽수 (30-32)로 분리됩니다.
                </p>
                <div className="cp-modal-body">
                  <label htmlFor="cp-program-page-split-delimiter" className="cp-program-page-split-field-label">
                    구분 기호
                  </label>
                  <input
                    ref={pageSplitDelimiterInputRef}
                    id="cp-program-page-split-delimiter"
                    type="text"
                    className="cp-program-page-split-delimiter-input"
                    value={pageSplitDelimiterInput}
                    onChange={(ev) => setPageSplitDelimiterInput(ev.target.value)}
                    onKeyDown={(ev) => {
                      const native = ev.nativeEvent as KeyboardEvent;
                      if (native.isComposing) return;
                      if (ev.key === 'Escape') {
                        ev.preventDefault();
                        setPageSplitModalOpen(false);
                        return;
                      }
                      if (ev.key !== 'Enter') return;
                      ev.preventDefault();
                      if (!pageSplitDelimiterInput.length) return;
                      commitPageAssistantSplit();
                    }}
                    placeholder="예: ( 또는 공백"
                    autoComplete="off"
                  />
                </div>
                <div className="cp-modal-footer">
                  <button type="button" className="cp-btn-secondary" onClick={() => setPageSplitModalOpen(false)}>
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-btn-primary"
                    disabled={!pageSplitDelimiterInput.length}
                    onClick={commitPageAssistantSplit}
                  >
                    분리하기
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </section>
  );
};

export default AdminLearningContentTable;
